import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import PublicIcon from '@mui/icons-material/Public';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import ExtensionIcon from '@mui/icons-material/Extension';
import DataObjectIcon from '@mui/icons-material/DataObject';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import {
    Box,
    Chip,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    Popover,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

/**
 * PlaceholderPicker — F7 Fase 1
 *
 * Popover unificado com as 6 categorias de placeholders do sistema:
 *   - param       (%param%)          — dinâmico, do fluxo
 *   - variable    (@variavel@)       — dinâmico, do fluxo
 *   - customField ($custom$)         — dinâmico, do fluxo
 *   - chat        ({{chat.*}})       — estático, contexto do chat corrente
 *   - system      ({{system.*}})     — estático, data/hora/hirer/etc
 *   - session     ({{session.user.*}})— estático, atendente atribuído
 *
 * Fonte: endpoint /api/chatbot-flow/get-placeholders (retorna as 6 categorias
 * agregadas). Cada categoria vira uma aba. Prop `categories: string[]` filtra
 * quais mostrar (default: todas). Ex: `categories={['chat','system','session']}`
 * pra caixas de output pro cliente, ou `categories={['chat']}` pra retrocompat
 * do ChatVariablesPicker.
 *
 * Nomenclatura: "placeholder" (nunca "token" — token = JWT no projeto).
 */

type PlaceholderItem = {
    category: string;   // "param" | "variable" | "customField" | "chat" | "system" | "session"
    key: string;        // "chat.customer.email"
    wrap: string;       // "{{chat.customer.email}}" ou "%plano%" etc — pronto pra inserir
    id?: number | null; // só pra dinâmicos
    label?: string;     // help/description
};

type Props = {
    open: boolean;
    anchorEl: HTMLElement | null;
    onClose: () => void;
    onInsert: (placeholder: string) => void;
    /** Filtra quais categorias mostrar. Default: todas as 6. */
    categories?: string[];
    /** Se fornecido, usa essa lista em vez de fetchar. Útil pra testes ou
     * quando o consumidor já tem os placeholders em mãos (evita re-fetch). */
    items?: PlaceholderItem[];
    /** Callback pra fetchar itens da API. Default: chama /chatbot-flow/get-placeholders. */
    fetchItems?: () => Promise<PlaceholderItem[]>;
};

const ALL_CATEGORIES = ['chat', 'system', 'session', 'param', 'variable', 'customField'];

const CATEGORY_META: Record<string, { labelKey: string; defaultLabel: string; Icon: any }> = {
    chat:        { labelKey: 'placeholder.category.chat',        defaultLabel: 'Chat',            Icon: PublicIcon },
    system:      { labelKey: 'placeholder.category.system',      defaultLabel: 'Sistema',         Icon: SettingsIcon },
    session:     { labelKey: 'placeholder.category.session',     defaultLabel: 'Sessão',          Icon: PersonIcon },
    param:       { labelKey: 'placeholder.category.param',       defaultLabel: 'Parâmetros',     Icon: ExtensionIcon },
    variable:    { labelKey: 'placeholder.category.variable',    defaultLabel: 'Variáveis',      Icon: DataObjectIcon },
    customField: { labelKey: 'placeholder.category.customField', defaultLabel: 'Campos',          Icon: DashboardCustomizeIcon },
};

const defaultFetch = async (): Promise<PlaceholderItem[]> => {
    // Import dinâmico pra não puxar a service em contextos que passam `items` explícito.
    const mod = await import('src/services/2talk/chatbot-flow.service');
    // API 2Talk é lowercase — `getPlaceholders` retorna wrapper GenericCommandResult.
    const res: any = await (mod as any).getPlaceholders?.();
    const raw: any[] = res?.data ?? res ?? [];
    return (Array.isArray(raw) ? raw : []).map((it: any) => ({
        category: String(it.category ?? ''),
        key: String(it.key ?? ''),
        wrap: String(it.wrap ?? ''),
        id: it.id != null ? Number(it.id) : null,
        label: String(it.label ?? ''),
    }));
};

const PlaceholderPicker: React.FC<Props> = ({
    open, anchorEl, onClose, onInsert,
    categories,
    items,
    fetchItems,
}) => {
    const intl = useIntl();
    const [query, setQuery] = useState('');
    const [tab, setTab] = useState(0);
    const [loaded, setLoaded] = useState<PlaceholderItem[]>([]);
    const [loading, setLoading] = useState(false);

    // Categorias efetivas (filtro + ordem canônica).
    const activeCategories = useMemo(() => {
        const filter = categories && categories.length > 0 ? categories : ALL_CATEGORIES;
        return ALL_CATEGORIES.filter(c => filter.includes(c));
    }, [categories]);

    // Fetcher: lazy, uma vez por abertura do popover.
    useEffect(() => {
        if (!open) return;
        if (items && items.length > 0) { setLoaded(items); return; }
        setLoading(true);
        const fn = fetchItems ?? defaultFetch;
        fn()
            .then(list => setLoaded(list ?? []))
            .catch(() => setLoaded([]))
            .finally(() => setLoading(false));
    }, [open, items, fetchItems]);

    // Filtrado por aba ativa + busca.
    const currentCategory = activeCategories[tab] ?? activeCategories[0];
    const filteredItems = useMemo(() => {
        const q = query.trim().toLowerCase();
        return loaded
            .filter(it => it.category === currentCategory)
            .filter(it => !q
                || it.key.toLowerCase().includes(q)
                || (it.label ?? '').toLowerCase().includes(q)
                || it.wrap.toLowerCase().includes(q));
    }, [loaded, currentCategory, query]);

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
                sx: {
                    width: 420,
                    maxHeight: 520,
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: 3,
                    borderRadius: 2,
                },
            }}
        >
            {/* Abas por categoria */}
            <Tabs
                value={Math.min(tab, activeCategories.length - 1)}
                onChange={(_, v) => { setTab(v); setQuery(''); }}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ minHeight: 36, borderBottom: '1px solid rgba(0,0,0,0.08)' }}
            >
                {activeCategories.map(cat => {
                    const meta = CATEGORY_META[cat];
                    const Icon = meta.Icon;
                    return (
                        <Tab
                            key={cat}
                            icon={<Icon sx={{ fontSize: 14 }} />}
                            iconPosition="start"
                            label={intl.formatMessage({ id: meta.labelKey, defaultMessage: meta.defaultLabel })}
                            sx={{ minHeight: 36, fontSize: 12 }}
                        />
                    );
                })}
            </Tabs>

            {/* Busca */}
            <Box sx={{ p: 1, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                <TextField
                    fullWidth
                    size="small"
                    autoFocus
                    placeholder={intl.formatMessage({
                        id: 'placeholder.search.placeholder',
                        defaultMessage: 'Buscar placeholder...',
                    })}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {/* Lista */}
            <Box sx={{ overflowY: 'auto', flex: 1 }}>
                {loading && (
                    <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                        <Typography variant="body2">Carregando placeholders…</Typography>
                    </Box>
                )}
                {!loading && filteredItems.length === 0 && (
                    <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                        <Typography variant="body2">
                            {query
                                ? intl.formatMessage({ id: 'placeholder.empty.search', defaultMessage: 'Nada encontrado.' })
                                : intl.formatMessage({ id: 'placeholder.empty.category', defaultMessage: 'Nenhum item nesta categoria.' })}
                        </Typography>
                    </Box>
                )}
                {!loading && filteredItems.length > 0 && (
                    <List dense disablePadding>
                        {filteredItems.map(item => (
                            <ListItem
                                key={`${item.category}-${item.key}-${item.id ?? ''}`}
                                secondaryAction={
                                    <Tooltip title={intl.formatMessage({ id: 'placeholder.action.insert', defaultMessage: 'Inserir' })}>
                                        <IconButton edge="end" size="small" color="primary" onClick={() => onInsert(item.wrap)}>
                                            <AddIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                }
                                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                                onClick={() => onInsert(item.wrap)}
                            >
                                <ListItemText
                                    primary={
                                        <Chip
                                            label={item.wrap}
                                            size="small"
                                            variant="outlined"
                                            sx={{ fontFamily: 'monospace', fontSize: 11 }}
                                        />
                                    }
                                    secondary={item.label ?? item.key}
                                    secondaryTypographyProps={{ variant: 'caption', sx: { display: 'block', mt: 0.25 } }}
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
            </Box>
        </Popover>
    );
};

export default PlaceholderPicker;
